import request from "../base-request";

/**
 * Creates a new user selection object
 * @returns The ID of the new user selection object
 */
export const createUserSelection = async () => {
	const [data] = await request<{ user_selection_id: string }>("/api/user_selection", {
		method: "POST",
	});

	return data;
};

/**
 * Gets the current page of users for a user selection object
 */
export interface GetUserSelectionUsersProps {
	selectionObjectID: string;
	searchTerm?: string;
	page?: number;
	fieldIds?: string[];
	letter?: string;
	limitSelected?: boolean;
	limitToDirectReports?: boolean;
	exclusionList?: string[];
	usersToExcludeSelectionId?: string;
	excludeCurrentUser?: boolean;
	filters?: { [key: string]: string | Array<string> };
	lastName?: boolean;
	sortField?: string;
	sortDirection?: string;
	limit?: number;
	onRemoveSuccess?: (data: UserSelectionMutationResponse) => void;
	onRemoveError?: (error: Error) => void;
}

export interface UserSelectionUsersResponse {
	custom_fields: { _id: string; name: string; type: string }[];
	departments: { _id: string; name: string; numUsers: number }[];
	page_count: number;
	display_user_map: Record<string, { name: string; active: boolean }>;
	selected_users: Record<string, boolean>;
	users: {
		_id: string;
		name: string;
		data_fields: Record<string, { field_name: string; value: any }>;
		admin_fields: {
			admin: boolean;
			basic_admin: boolean;
			billing_admin: boolean;
			deactivated: boolean;
			invited: boolean;
			last_seen: string;
		};
	}[];
}

export const getUserSelectionUsers = async (props: GetUserSelectionUsersProps): Promise<UserSelectionUsersResponse> => {
	const { selectionObjectID } = props;

	const queryParams = new URLSearchParams();

	if (props.searchTerm) {
		queryParams.set("q", props.searchTerm);
	}

	if (props.page) {
		queryParams.set("page", props.page.toString());
	}

	if (props.fieldIds && props.fieldIds.length > 0) {
		queryParams.set("fieldIds[]", props.fieldIds.join(","));
	}

	if (props.letter) {
		queryParams.set("letter", props.letter);
	}

	if (props.limitSelected) {
		queryParams.set("limit_selected", "true");
	}

	if (props.limitToDirectReports) {
		queryParams.set("limit_to_direct_reports", "true");
	}

	if (props.exclusionList && props.exclusionList.length > 0) {
		queryParams.set("exclusionList[]", props.exclusionList.join(","));
	}

	if (props.usersToExcludeSelectionId) {
		queryParams.set("usersToExcludeSelectionId", props.usersToExcludeSelectionId);
	}

	if (props.excludeCurrentUser) {
		queryParams.set("excludeCurrentUser", "true");
	}

	if (props.filters) {
		Object.keys(props.filters).forEach((filter) => {
			if (props.filters?.[filter]) {
				let queryParam = "";
				let queryFilterKey = "";
				if (props.filters[filter] instanceof Array) {
					queryParam = (props.filters[filter] as Array<string>).join(",");
					queryFilterKey = `${filter}[]`;
				} else {
					queryParam = props.filters[filter] as string;
					queryFilterKey = filter;
				}
				queryParams.set(queryFilterKey, queryParam);
			}
		});
	}

	if (props.lastName !== undefined) {
		queryParams.set("last_name", props.lastName.toString());
	}

	// Add sorting parameters back
	if (props.sortField) {
		queryParams.set("sort_field", props.sortField);
	}

	if (props.sortDirection) {
		queryParams.set("sort_direction", props.sortDirection);
	}

	if (props.limit) {
		queryParams.set("limit", props.limit.toString());
	}

	const url = `/api/user/${selectionObjectID}?${queryParams.toString()}`;
	const [data] = await request<UserSelectionUsersResponse>(url);

	return data;
};

/**
 * Props for adding users to a user selection
 */
export interface AddUsersToSelectionProps {
	selectionObjectID: string;
	users?: string[];
	groups?: string[];
	allUsers?: boolean;
	filters?: {
		canAssignGoalsTo?: boolean;
		canAssignReviewsTo?: boolean;
	};
	exclusionList?: string[];
	usersToExcludeSelectionId?: string;
}

/**
 * Response from add/remove user selection operations
 */
export interface UserSelectionMutationResponse {
	success: boolean;
	skipped?: string[];
}

/**
 * Response from count user selection operation
 */
export interface UserSelectionCountResponse {
	count: number;
}

/**
 * Adds users to a user selection object
 */
export const addUsersToSelection = async (props: AddUsersToSelectionProps): Promise<UserSelectionMutationResponse> => {
	const { selectionObjectID } = props;

	const payload: Record<string, any> = {};

	if (props.users && props.users.length > 0) {
		payload.users = props.users;
	}

	if (props.groups && props.groups.length > 0) {
		payload.groups = props.groups;
	}

	if (props.allUsers) {
		payload.all_users = props.allUsers;
	}

	if (props.filters) {
		payload.filters = props.filters;
	}

	if (props.exclusionList && props.exclusionList.length > 0) {
		payload.exclusionList = props.exclusionList;
	}

	if (props.usersToExcludeSelectionId) {
		payload.usersToExcludeSelectionId = props.usersToExcludeSelectionId;
	}

	const [data] = await request<UserSelectionMutationResponse>(`/api/user_selection/${selectionObjectID}/add`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(payload),
	});

	return data;
};

/**
 * Props for removing users from a user selection
 */
export interface RemoveUsersFromSelectionProps {
	selectionObjectID: string;
	users: string[];
	allUsers?: boolean;
}

/**
 * Removes users from a user selection object
 */
export const removeUsersFromSelection = async (
	props: RemoveUsersFromSelectionProps
): Promise<UserSelectionMutationResponse> => {
	const { selectionObjectID } = props;

	const [data] = await request<UserSelectionMutationResponse>(`/api/user_selection/${selectionObjectID}/remove`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(props),
	});

	return data;
};

/**
 * Gets the count of selected users in a user selection object
 */
export const getUserSelectionCount = async (selectionObjectID: string): Promise<UserSelectionCountResponse> => {
	const [data] = await request<UserSelectionCountResponse>(`/api/user_selection/${selectionObjectID}/count`);
	return data;
};
